const CONNECTION_EXHAUSTION_ERROR_CODES = new Set([
  'EHOSTUNREACH',
  'EADDRNOTAVAIL',
  'UND_ERR_SOCKET',
]);

const TRANSIENT_ERROR_CODES = new Set([
  ...CONNECTION_EXHAUSTION_ERROR_CODES,
  'ECONNRESET',
  'ECONNREFUSED',
  'ETIMEDOUT',
  'ENOTFOUND',
  'UND_ERR_CONNECT_TIMEOUT',
  'UND_ERR_HEADERS_TIMEOUT',
  'UND_ERR_BODY_TIMEOUT',
]);

function getErrorCauseCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const { cause } = error as { cause?: unknown };
  if (!cause || typeof cause !== 'object') {
    return undefined;
  }

  const { code } = cause as { code?: unknown };
  return typeof code === 'string' ? code : undefined;
}

function isTransientErrorObject(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const { code } = error as { code?: unknown };
  if (typeof code === 'string' && TRANSIENT_ERROR_CODES.has(code)) {
    return true;
  }

  const causeCode = getErrorCauseCode(error);
  if (causeCode && TRANSIENT_ERROR_CODES.has(causeCode)) {
    return true;
  }

  if (error instanceof TypeError && error.message.includes('fetch failed')) {
    return true;
  }

  const { errors } = error as { errors?: unknown[] };
  if (Array.isArray(errors)) {
    return errors.some(isTransientErrorObject);
  }

  return false;
}

function isTransientFetchError(error: unknown): boolean {
  return isTransientErrorObject(error);
}

export function isConnectionExhaustionError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const { code } = error as { code?: unknown };
  if (typeof code === 'string' && CONNECTION_EXHAUSTION_ERROR_CODES.has(code)) {
    return true;
  }

  const causeCode = getErrorCauseCode(error);
  if (causeCode && CONNECTION_EXHAUSTION_ERROR_CODES.has(causeCode)) {
    return true;
  }

  const { errors } = error as { errors?: unknown[] };
  if (Array.isArray(errors)) {
    return errors.some(isConnectionExhaustionError);
  }

  return false;
}

export function shouldRetryTransientFetchError(error: unknown): boolean {
  return isTransientFetchError(error) && !isConnectionExhaustionError(error);
}

export default isTransientFetchError;
